import useStyles from "./styles";
import {
  Backdrop,
  Box,
  CircularProgress,
  Fade,
  Grid,
  TextField,
  Typography
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { ImageEditor } from "../../../../components/editors/ImageEditor";
import { StepProps } from "./types";
import { StepButtons } from "./components/StepButtons";
import * as tenantApi from "../../../../api/tenantApi";
import { isOk } from "../../../../result";
import { Tenant, TenantEndUser } from "../../../../models/Tenant";
import { ImageInformation } from "../../../../models/ImageInformation";
import { Alert } from "@material-ui/lab";
import { useI18n } from "../../../../hooks/useI18n";

export const Company = ({ onNext }: StepProps) => {
  const classes = useStyles();
  const { t } = useI18n();

  const [tenant, setTenant] = useState({} as Tenant);
  const [editTenant, setEditTenant] = useState({} as TenantEndUser);
  const [validationErrors, setValidationErrors] = useState({} as any);
  const [fatalError, setFatalError] = useState({} as any);
  const [logoLoading, setLogoLoading] = useState(false);
  const [bannerLoading, setBannerLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    tenantApi.getCurrentTenant().then(result => {
      if (isOk(result)) {
        setTenant(result.value);
        setLoading(false);
        populateTenantEndUser(result.value);
      }
    });
  }, []);

  const upload = async (
    id: string,
    image: ImageInformation,
    type: string
  ): Promise<string> => {
    if (!image?.blob) return "";

    const result = await tenantApi.uploadImage(id, image.blob, type);

    if (result.type !== "OK") {
      setFatalError(result.details);
      setLoading(false);
      return "";
    }

    return result.value;
  };

  const populateTenantEndUser = (x: Tenant): TenantEndUser => {
    let t = {} as TenantEndUser;

    t.companyName = x.companyName;
    t.contactName = x.contactName;
    t.contactEmail = x.contactEmail;
    t.orgNo = x.orgNo;
    t.address = x.address;
    t.postalCode = x.postalCode;
    t.city = x.city;
    t.iban = x.iban;
    t.bic = x.bic;

    setEditTenant(t);
    return t;
  };

  const shouldUpdate = (): boolean => {
    return (
      tenant.companyName != editTenant.companyName ||
      tenant.contactName != editTenant.contactName ||
      tenant.contactEmail != editTenant.contactEmail ||
      tenant.orgNo != editTenant.orgNo ||
      tenant.address != editTenant.address ||
      tenant.postalCode != editTenant.postalCode ||
      tenant.city != editTenant.city ||
      tenant.iban != editTenant.iban ||
      tenant.bic != editTenant.bic
    );
  };

  const saveCompanyInformation = async () => {
    setLoading(true);
    if (shouldUpdate()) {
      const t = populateTenantEndUser(tenant);

      const result = await tenantApi.endUserUpdateTenant(t);
      if (result.type !== "OK") {
        setLoading(false);

        if (result.details.errors) {
          setValidationErrors(result.details.errors);
        }

        if (result.details.status !== 400) {
          setFatalError(result.details);
        }
        return;
      }
    }

    setLoading(false);
    onNext();
  };

  return loading ? (
    <div
      style={{
        width: "100%",
        height: "100%",
        minHeight: "600px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      <Fade
        in={true}
        style={{
          minHeight: 100,
          transitionDelay: "800ms"
        }}
        unmountOnExit
      >
        <div style={{ display: "flex", justifyContent: "center" }}>
          <CircularProgress size={80} />
        </div>
      </Fade>
    </div>
  ) : (
    <>
      <Typography variant={"h5"}>
        {t("common.messages.companyInformation")}
      </Typography>
      <Typography className={classes.onBoardingTitle} variant={"subtitle1"}>
        Please enter your company information below
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <TextField
            required
            error={validationErrors["contactName"] != undefined}
            helperText={
              validationErrors["contactName"] != undefined
                ? validationErrors["contactName"]
                : ""
            }
            className={classes.gridField}
            label={t("common.formFields.contactName")}
            value={tenant.contactName ?? ""}
            onChange={e => {
              setValidationErrors({
                ...validationErrors,
                contactName: undefined
              });
              setTenant({
                ...tenant,
                contactName: e.target.value
              });
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            required
            error={validationErrors["contactEmail"] != undefined}
            helperText={
              validationErrors["contactEmail"] != undefined
                ? validationErrors["contactEmail"]
                : ""
            }
            className={classes.gridField}
            label={t("common.formFields.contactEmail")}
            value={tenant.contactEmail ?? ""}
            onChange={e => {
              setValidationErrors({
                ...validationErrors,
                contactEmail: undefined
              });
              setTenant({
                ...tenant,
                contactEmail: e.target.value
              });
            }}
          />
        </Grid>
        <Grid item xs={12} md={8}>
          <TextField
            required
            error={validationErrors["companyName"] != undefined}
            helperText={
              validationErrors["companyName"] != undefined
                ? validationErrors["companyName"]
                : ""
            }
            className={classes.gridField}
            label={t("common.formFields.companyName")}
            value={tenant.companyName ?? ""}
            onChange={e => {
              setValidationErrors({
                ...validationErrors,
                companyName: undefined
              });
              setTenant({
                ...tenant,
                companyName: e.target.value
              });
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            required
            error={validationErrors["orgNo"] != undefined}
            helperText={
              validationErrors["orgNo"] != undefined
                ? validationErrors["orgNo"]
                : ""
            }
            className={classes.gridField}
            label="Org. no."
            value={tenant.orgNo ?? ""}
            onChange={e => {
              setValidationErrors({ ...validationErrors, orgNo: undefined });
              setTenant({
                ...tenant,
                orgNo: e.target.value
              });
            }}
          />
        </Grid>

        <Grid item xs={12} md={5}>
          <TextField
            required
            error={validationErrors["address"] != undefined}
            helperText={
              validationErrors["address"] != undefined
                ? validationErrors["address"]
                : ""
            }
            className={classes.gridField}
            label="Address"
            value={tenant.address ?? ""}
            onChange={e => {
              setValidationErrors({ ...validationErrors, address: undefined });
              setTenant({
                ...tenant,
                address: e.target.value
              });
            }}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <TextField
            required
            error={validationErrors["postalCode"] != undefined}
            helperText={
              validationErrors["postalCode"] != undefined
                ? validationErrors["postalCode"]
                : ""
            }
            className={classes.gridField}
            label={t("common.formFields.postalCode")}
            value={tenant.postalCode ?? ""}
            onChange={e => {
              setValidationErrors({
                ...validationErrors,
                postalCode: undefined
              });
              setTenant({
                ...tenant,
                postalCode: e.target.value
              });
            }}
          />
        </Grid>
        <Grid item xs={12} md={5}>
          <TextField
            required
            error={validationErrors["city"] != undefined}
            helperText={
              validationErrors["city"] != undefined
                ? validationErrors["city"]
                : ""
            }
            className={classes.gridField}
            label="City"
            value={tenant.city ?? ""}
            onChange={e => {
              setValidationErrors({ ...validationErrors, city: undefined });
              setTenant({
                ...tenant,
                city: e.target.value
              });
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            error={validationErrors["iban"] != undefined}
            helperText={
              validationErrors["iban"] != undefined
                ? validationErrors["iban"]
                : ""
            }
            className={classes.gridField}
            label="IBAN"
            value={tenant.iban ?? ""}
            onChange={e => {
              setValidationErrors({ ...validationErrors, iban: undefined });
              setTenant({
                ...tenant,
                iban: e.target.value
              });
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            error={validationErrors["bic"] != undefined}
            helperText={
              validationErrors["bic"] != undefined
                ? validationErrors["bic"]
                : ""
            }
            className={classes.gridField}
            label="BIC/Swift"
            value={tenant.bic ?? ""}
            onChange={e => {
              setValidationErrors({ ...validationErrors, bic: undefined });
              setTenant({
                ...tenant,
                bic: e.target.value
              });
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ImageEditor
            loading={logoLoading}
            title={"Logo"}
            helpText={"Use a square image for best result"}
            dropZoneClass={classes.dropZone}
            dropZoneTextClass={classes.dropzoneTextClass}
            acceptedFiles={[
              "image/png",
              "image/jpg",
              "image/jpeg",
              "image/gif",
              "image/heic"
            ]}
            initial={{
              url: tenant.logoUrl ?? ""
            }}
            onChange={async data => {
              setLogoLoading(true);
              const result = await upload(tenant.id, data, "logo");
              setTenant({ ...tenant, logoUrl: result });
              setLogoLoading(false);
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ImageEditor
            title={"Banner"}
            loading={bannerLoading}
            helpText={"Use a rectangular image for best result"}
            dropZoneClass={classes.dropZone}
            dropZoneTextClass={classes.dropzoneTextClass}
            acceptedFiles={[
              "image/png",
              "image/jpg",
              "image/jpeg",
              "image/gif",
              "image/heic"
            ]}
            initial={{
              url: tenant.bannerUrl ?? ""
            }}
            onChange={async data => {
              setBannerLoading(true);
              const result = await upload(tenant.id, data, "banner");
              setTenant({ ...tenant, bannerUrl: result });
              setBannerLoading(false);
            }}
          />
        </Grid>
      </Grid>
      {fatalError.title && (
        <Alert severity={"error"} title={fatalError.title}>
          {fatalError.detail}
        </Alert>
      )}
      <StepButtons
        nextDisabled={loading}
        backDisabled={true}
        onClickNext={() => saveCompanyInformation()}
      />
    </>
  );
};
